// Client Supabase unique de l'application (singleton).
//
// Les identifiants proviennent des variables d'environnement publiques
// injectées par la plateforme (safe côté navigateur) :
//   - VITE_PUBLIC_SUPABASE_URL
//   - VITE_PUBLIC_SUPABASE_ANON_KEY
//
// Un seul client est créé pour toute l'application : on ne l'instancie jamais
// ailleurs, afin d'éviter plusieurs sessions Auth concurrentes.

import { createClient, type Session, type User } from "@supabase/supabase-js";

const supabaseUrl = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined) ?? "";
const supabaseAnonKey = (import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined) ?? "";

if (!supabaseUrl || !supabaseAnonKey) {
  // La configuration est normalement injectée par la plateforme. On prévient
  // sans bloquer le rendu pour faciliter le diagnostic.
  console.warn(
    "[Supabase] Configuration manquante : VITE_PUBLIC_SUPABASE_URL et VITE_PUBLIC_SUPABASE_ANON_KEY sont requis."
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/* ------------------------------------------------------------------ */
/* Récupération de session invalide (refresh token périmé)             */
/* ------------------------------------------------------------------ */

/**
 * Détecte l'erreur envoyée par Supabase Auth lorsqu'un refresh token stocké
 * n'est plus reconnu côté serveur (rotation déjà consommée, session d'un ancien
 * projet, nettoyage serveur, etc.). Dans ce cas la session locale est morte et
 * doit être purgée pour permettre une reconnexion propre.
 */
export function isInvalidRefreshTokenError(value: unknown): boolean {
  const message =
    typeof value === "string"
      ? value
      : value instanceof Error
        ? value.message
        : ((value as { message?: string } | null)?.message ?? "");
  const m = message.toLowerCase();
  return (
    m.includes("invalid refresh token") ||
    m.includes("refresh token not found") ||
    m.includes("refresh_token_not_found") ||
    m.includes("invalid_grant") ||
    (m.includes("refresh token") && m.includes("not found"))
  );
}

// Empêche les purges concurrentes (plusieurs ticks de refresh en parallèle).
let recovering = false;

/**
 * Purge uniquement la session locale invalide (aucune donnée métier touchée).
 * `scope: "local"` nettoie le stockage navigateur sans appel réseau.
 */
export function clearInvalidSession(): void {
  if (recovering) return;
  recovering = true;
  void supabase.auth
    .signOut({ scope: "local" })
    .catch(() => undefined)
    .finally(() => {
      recovering = false;
    });
}

// Filet de sécurité : le renouvellement de token en arrière-plan est lancé par
// un timer qui n'est pas `await`é. Une erreur y devient une promesse rejetée non
// gérée. On l'intercepte ici pour éviter la remontée d'erreur + purger la
// session morte, au lieu de laisser l'application bloquée avec un token invalide.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (isInvalidRefreshTokenError(event.reason)) {
      event.preventDefault();
      clearInvalidSession();
    }
  });

  // Même filet pour les erreurs remontées de façon synchrone (certaines
  // plateformes capturent via l'évènement `error` global).
  window.addEventListener("error", (event) => {
    if (isInvalidRefreshTokenError(event.error) || isInvalidRefreshTokenError(event.message)) {
      clearInvalidSession();
    }
  });
}

/* ------------------------------------------------------------------ */
/* Lecture d'identité SANS lever d'erreur                               */
/* ------------------------------------------------------------------ */

/**
 * Récupère l'utilisateur connecté sans jamais lever d'exception.
 *
 * Un refresh token stocké mais périmé (rotation déjà consommée, session d'un
 * autre environnement, nettoyage serveur…) fait échouer le SDK Supabase avec
 * « Invalid Refresh Token: Refresh Token Not Found ». On purge alors la session
 * locale et on retourne `null`, au lieu de laisser l'exception casser l'écran :
 * l'utilisateur est simplement considéré comme non connecté.
 */
export async function getSafeUser(): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error && isInvalidRefreshTokenError(error)) {
      clearInvalidSession();
      return null;
    }
    return data.user ?? null;
  } catch (error) {
    if (isInvalidRefreshTokenError(error)) clearInvalidSession();
    return null;
  }
}

/** Récupère la session locale sans jamais lever d'exception (cf. getSafeUser). */
export async function getSafeSession(): Promise<Session | null> {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error && isInvalidRefreshTokenError(error)) {
      clearInvalidSession();
      return null;
    }
    return data.session ?? null;
  } catch (error) {
    if (isInvalidRefreshTokenError(error)) clearInvalidSession();
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Initialisation Auth : neutralisation du rejet non géré              */
/* ------------------------------------------------------------------ */

/**
 * Dès que le client est créé, le SDK Supabase démarre en ARRIÈRE-PLAN une
 * « initialize » (relecture de la session stockée + éventuel renouvellement de
 * jeton). Sur une session périmée (refresh token révoqué : rotation déjà
 * consommée, nettoyage serveur, ancien projet…), cette promesse interne se
 * rejette AVANT que nos appels `getSafeSession` / `getSafeUser` n'y attachent un
 * gestionnaire. Résultat : une promesse rejetée non gérée, remontée par la
 * plateforme comme « Invalid Refresh Token: Refresh Token Not Found », alors
 * même que l'application traite déjà l'utilisateur comme non connecté.
 *
 * On attache donc IMMÉDIATEMENT un gestionnaire de rejet à cette initialisation :
 * la session morte est purgée (aucune donnée métier touchée) et plus aucune
 * erreur non gérée ne remonte. L'utilisateur est simplement considéré comme
 * déconnecté, jusqu'à une reconnexion normale.
 *
 * Appel défensif : `initialize()` est idempotent (il renvoie la promesse déjà
 * en cours). S'il n'est pas exposé par la version installée, le garde `typeof`
 * évite tout crash — le filet global plus bas reste actif en complément.
 */
type AuthInitializable = { initialize?: () => Promise<unknown> };
const authInitializable = supabase.auth as unknown as AuthInitializable;
if (typeof authInitializable.initialize === "function") {
  void authInitializable.initialize().catch(() => {
    clearInvalidSession();
  });
}